// "My Big Three" story card (1080x1920), drawn entirely on the device: an
// SVG backdrop painted to a canvas, the text drawn over it in the app's own
// faces, exported as PNG. No server, no upload.
import { GLYPH_PATHS } from "../components/star-charts/constants";
import { BIG_THREE } from "../content/astro/bigThree";
import { sr } from "../shared/rng";

export const W = 1080;
export const H = 1920;
export const esc = (t) => String(t).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// Greedy wrap by an approximate character width for the given font size.
export function wrap(text, fontSize, maxWidth, factor = 0.5) {
  const maxChars = Math.floor(maxWidth / (fontSize * factor));
  const words = String(text).split(/\s+/);
  const lines = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length > maxChars) { if (cur) lines.push(cur); cur = w; } else cur = (cur + " " + w).trim();
  }
  if (cur) lines.push(cur);
  return lines;
}

export function glyph(signKey, x, y, size, color) {
  const paths = GLYPH_PATHS[signKey];
  if (!paths) return "";
  const s = size / 24;
  return `<g transform="translate(${x},${y}) scale(${s})" fill="none" stroke="${color}" stroke-width="${1.4}" stroke-linecap="round" stroke-linejoin="round">${
    paths.map((p) => (p.d ? `<path d="${p.d}" ${p.fill ? `fill="${color}"` : ""}/>` : "")).join("")
  }</g>`;
}

export function starDots(seed) {
  const rng = sr(seed);
  return Array.from({ length: 160 }, () => {
    const x = rng() * W, y = rng() * H, r = 0.8 + rng() * 2.2, o = 0.15 + rng() * 0.55;
    return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${r.toFixed(1)}" fill="#E4DED4" opacity="${o.toFixed(2)}"/>`;
  }).join("");
}

// Night-sky backdrop shared by every story card.
export const CARD_DEFS = `<defs>
      <linearGradient id="bg" x1="0" y1="0" x2="0.3" y2="1"><stop offset="0" stop-color="#151833"/><stop offset="0.55" stop-color="#0C0E1A"/><stop offset="1" stop-color="#121426"/></linearGradient>
      <radialGradient id="glow" cx="0.5" cy="0.12" r="0.6"><stop offset="0" stop-color="#7E8EC2" stop-opacity="0.35"/><stop offset="1" stop-color="#7E8EC2" stop-opacity="0"/></radialGradient>
    </defs>
    <rect width="${W}" height="${H}" fill="url(#bg)"/>
    <rect width="${W}" height="${H}" fill="url(#glow)"/>`;

// The card's text is drawn on the canvas, not in the SVG: an SVG painted to a
// canvas can't load web fonts, but the canvas can use the faces the app has
// already loaded (public/fonts) and measure every line with measureText.
export const SANS = "'Geist', 'Helvetica Neue', sans-serif";
export const SERIF = "'Fraunces', Georgia, serif";
const MARGIN = 60;            // clear space on both sides of every line
const TEXT_X = 290;           // where the Big Three lines start, right of the glyph
const COLOR = { text: "#E4DED4", label: "#9AA6D6", sub: "#A8A098" };
const FACES = [`600 36px ${SANS}`, `500 32px ${SANS}`, `400 30px ${SANS}`, `400 78px ${SERIF}`, `italic 400 46px ${SERIF}`];

/** Greedy wrap by measured width in the context's current font. */
export function measureWrap(ctx, text, maxWidth) {
  const lines = [];
  let cur = "";
  for (const w of String(text).split(/\s+/).filter(Boolean)) {
    const next = cur ? `${cur} ${w}` : w;
    if (cur && ctx.measureText(next).width > maxWidth) { lines.push(cur); cur = w; } else cur = next;
  }
  if (cur) lines.push(cur);
  return lines;
}

/** Load the card's faces before drawing; a canvas falls back silently
    otherwise. Adds the app's font stylesheet first if the page lacks it. */
export async function loadFaces() {
  if (!document.fonts?.load) return;
  const has = (name) => [...document.fonts].some((f) => f.family.replace(/['"]/g, "") === name);
  if (!has("Fraunces") || !has("Geist")) {
    await new Promise((resolve) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = `${process.env.PUBLIC_URL || ""}/fonts/fonts.css`;
      link.onload = link.onerror = resolve;
      document.head.appendChild(link);
    });
  }
  try { await Promise.all(FACES.map((f) => document.fonts.load(f, "The Web Aa"))); } catch (e) { /* draw with fallbacks */ }
}

function bigThreeRows(natal) {
  const P = natal.points;
  return [
    { role: "sun", label: natal.sunRange ? `Sun in ${natal.sunRange.from} or ${natal.sunRange.to}` : `Sun in ${P.sun.sign}`, sign: P.sun.signKey },
    { role: "moon", label: natal.moonRange ? `Moon in ${natal.moonRange.from} or ${natal.moonRange.to}` : `Moon in ${P.moon.sign}`, sign: P.moon.signKey },
    P.asc ? { role: "rising", label: `${P.asc.sign} rising`, sign: P.asc.signKey } : null,
  ].filter(Boolean);
}

/** One footer line per tradition, e.g. "Maya day: 10 Kan (Seed)". */
export function tileLine(t) {
  return `${t.tradition}: ${t.value}${t.key === "mayan" && t.sub ? ` (${t.sub})` : ""}`;
}

/* Lay out every piece of the card with real measurements. Returns the
   shapes for the SVG backdrop (circles and glyphs) and the text runs for
   the canvas, both in card coordinates before centering. */
export function layoutBigThree(ctx, { natal, tiles = [] }) {
  const shapes = [];
  const runs = [];
  const center = W / 2, wide = W - MARGIN * 2;
  const put = (font, color, text, x, y, align = "left") => runs.push({ font, color, text, x, y, align });

  put(`600 36px ${SANS}`, COLOR.text, "The Web", center, 200, "center");
  put(`400 78px ${SERIF}`, COLOR.text, "My Big Three", center, 330, "center");

  let y = 470;
  for (const r of bigThreeRows(natal)) {
    const lineFont = `italic 400 46px ${SERIF}`;
    ctx.font = lineFont;
    const lines = measureWrap(ctx, BIG_THREE[r.role]?.[r.sign]?.line || "", W - MARGIN - TEXT_X).slice(0, 4);
    shapes.push(`<circle cx="170" cy="${y + 44}" r="78" fill="rgba(126,142,194,0.12)" stroke="#7E8EC2" stroke-opacity="0.6" stroke-width="2"/>`);
    shapes.push(glyph(r.sign, 120, y - 6, 100, COLOR.text));
    put(`500 32px ${SANS}`, COLOR.label, r.label, TEXT_X, y + 6);
    lines.forEach((l, i) => put(lineFont, COLOR.text, l, TEXT_X, y + 66 + i * 58));
    y += Math.max(250, 110 + lines.length * 58 + 70);
  }

  // Each tradition gets its own line. A long one (three Vedic mansions)
  // steps down in size before it would ever wrap.
  let ty = y + 30;
  for (const t of tiles) {
    const text = tileLine(t);
    let size = 30;
    ctx.font = `400 ${size}px ${SANS}`;
    while (size > 22 && ctx.measureText(text).width > wide) { size -= 2; ctx.font = `400 ${size}px ${SANS}`; }
    const parts = measureWrap(ctx, text, wide);
    parts.forEach((l) => { put(ctx.font, COLOR.sub, l, center, ty, "center"); ty += 46; });
  }

  const footerY = ty + (tiles.length ? 70 : 30);
  put(`500 34px ${SANS}`, COLOR.label, "Read yours at theweb.love", center, footerY, "center");
  return { shapes, runs, top: 160, bottom: footerY + 20 };
}

/* The night sky, star dots, glyph rings and glyphs. No text. */
function backdropSvg(shapes, birthSeed, transform) {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
    ${CARD_DEFS}
    ${starDots(birthSeed)}
    <g transform="${transform}">${shapes.join("")}</g>
  </svg>`;
}

export function loadSvgImage(svg) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

/** Paint the Big Three card onto a 1080x1920 canvas (made if not given). */
export async function drawBigThree({ natal, birthSeed = 1, tiles = [] }, canvas = document.createElement("canvas")) {
  await loadFaces();
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  const { shapes, runs, top, bottom } = layoutBigThree(ctx, { natal, tiles });

  // Center the composition; if it runs taller than the card, scale it down
  // around the middle so nothing leaves the card or its side margins.
  const room = H - 160;
  const k = Math.min(1, room / (bottom - top));
  const dx = (W / 2) * (1 - k);
  const dy = (H - (bottom - top) * k) / 2 - top * k;
  const transform = `translate(${dx.toFixed(1)},${dy.toFixed(1)}) scale(${k.toFixed(4)})`;

  ctx.drawImage(await loadSvgImage(backdropSvg(shapes, birthSeed, transform)), 0, 0, W, H);
  ctx.save();
  ctx.translate(dx, dy);
  ctx.scale(k, k);
  ctx.textBaseline = "alphabetic";
  // Each line's clear space on the left and right, in card pixels (read by
  // scripts/review/ba020-check.mjs).
  let left = W, right = W;
  for (const r of runs) {
    ctx.font = r.font;
    ctx.fillStyle = r.color;
    ctx.textAlign = r.align;
    ctx.fillText(r.text, r.x, r.y);
    const w = ctx.measureText(r.text).width;
    const x0 = r.align === "center" ? r.x - w / 2 : r.x;
    left = Math.min(left, dx + x0 * k);
    right = Math.min(right, W - (dx + (x0 + w) * k));
  }
  ctx.restore();
  canvas.dataset.sides = `${Math.floor(left)},${Math.floor(right)}`;
  return canvas;
}

export function svgToPngBlob(svg) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      canvas.getContext("2d").drawImage(img, 0, 0, W, H);
      URL.revokeObjectURL(url);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png");
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

/** Share via the system sheet when files are supported, else download. */
export async function shareSvgCard(svg, filename, text) {
  return sharePngBlob(await svgToPngBlob(svg), filename, text);
}

export async function sharePngBlob(blob, filename, text) {
  const file = new File([blob], filename, { type: "image/png" });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], text });
      return "shared";
    } catch (e) {
      if (e?.name === "AbortError") return "cancelled";
    }
  }
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  return "downloaded";
}

export async function shareBigThree({ natal, birthSeed, tiles }) {
  const canvas = await drawBigThree({ natal, birthSeed, tiles });
  const blob = await new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png"));
  return sharePngBlob(blob, "my-big-three.png", "My Big Three. Read yours at theweb.love");
}
